import { checkPermission } from "@/components/Guard/PermissionGuard";
import { IS_DEV } from "@/constants";
import usePermission from "@/hooks/usePermission";
import * as React from "react";
import { ActionColumnPermission, ITableHeaders } from "./table.interface";

/**
 * Whether the table's action column is being rendered. `<ActionCell />` reads
 * this to decide whether to emit its `<td>` at all.
 *
 * Defaults to `true` so anything rendered outside a `CustomTable` — the legacy
 * table, the expandable table, a row pulled into a dialog — keeps behaving
 * exactly as it did.
 */
export const ActionColumnContext = React.createContext(true);

export const useActionColumnVisible = () =>
  React.useContext(ActionColumnContext);

/**
 * The ids of the columns this table is currently rendering. `<Cell columnId />`
 * reads it to decide whether to emit its `<td>`.
 *
 * `null` means "no opinion" — every cell renders. That is the default, so rows
 * outside a `CustomTable`, and the many rows that don't use `<Cell />` at all,
 * are untouched.
 *
 * This exists because hiding a column is the same problem as hiding the action
 * column: the head and the cells are matched by position, so a header can only
 * disappear if the matching `<td>` disappears with it. Rows here are supplied by
 * the caller and render a fixed run of cells, so they have to opt in per cell.
 */
export const VisibleColumnsContext = React.createContext<Set<string> | null>(
  null
);

/**
 * Whether this table can drop columns at the user's choosing — a column picker,
 * or a caller driving `hiddenColumns` by hand. Capability, not current state: it
 * stays true while nothing is hidden.
 *
 * Only `<Cell />` reads it, and only to complain in development. `columnId` is
 * optional so that converting a list is a mechanical swap, which means a cell on
 * a picker-enabled list can silently go unnamed — and an unnamed cell always
 * emits its `<td>` while its header stays hideable. The first hide then puts
 * every column-position rule one column out and `sync()` disables the lot. This
 * turns that into a message the moment the row renders.
 */
export const ColumnsAreHideableContext = React.createContext(false);

export const useIsColumnVisible = (columnId?: string) => {
  const visible = React.useContext(VisibleColumnsContext);
  // An unnamed cell is always rendered. Conversion has to happen on every list
  // — `maxLines` only reaches a `<Cell />` wrapper, so a row left on
  // `StyledTableCell` would wrap with nothing bounding it — while only the few
  // lists that get a picker need their columns to be nameable. Requiring an id
  // everywhere would make the cheap half of that work expensive.
  return visible === null || columnId === undefined || visible.has(columnId);
};

/** The id CustomTable already uses to find the column it pins. */
const isActionId = (id: string) => /^actions?$/i.test(id);

/** Said once per offending header shape, not once per render. See the use below. */
const warnedAboutDuplicateActionColumns = new Set<string>();

/**
 * Resolves an action column's declared permission the same way `PermissionGuard`
 * resolves an individual button, so the column and the buttons inside it can
 * never disagree: same `checkPermission`, same `isAdmin` bypass, same `.some()`
 * over an array of modules.
 *
 * The `actions` array is OR'd, not AND'd — see `ActionColumnPermission`.
 */
function isPermitted(
  { module, actions }: ActionColumnPermission,
  permission: ReturnType<typeof usePermission>["permission"],
  isAdmin: boolean
) {
  if (isAdmin) return true;
  if (!module || !actions?.length) return true;
  const modules = Array.isArray(module) ? module : [module];
  return modules.some((name) =>
    actions.some((action) =>
      // `permission` is optional-chained out of the user object, so it can be
      // undefined outright. PermissionGuard gets away without this because it
      // only runs where someone placed a guard; this runs on every table.
      checkPermission(permission?.[name]?.global, action)
    )
  );
}

/**
 * Resolves whether this table has an action column at all: dropped when the user
 * holds none of the permissions that would put a button in it, and equally when
 * the header array never declared one.
 *
 * Returns the flag as well as the filtered array because BOTH ends have to move
 * together: the head renders `visibleHeaders`, and `<ActionCell />` reads
 * `actionColumnVisible` off context. One decision, two consumers — the previous
 * attempt at this filtered the header and left the cell, which broke every
 * column-position rule in the table.
 *
 * Deliberately NOT memoised. `headerKey` is derived from what this returns and is
 * the dep for every memo downstream, so a cache here would have to be keyed on
 * the permissions too — and permissions change without the table unmounting
 * (`UserContext` swaps the user object on refetch). Filtering a dozen headers per
 * render costs nothing next to getting that wrong.
 */
export function useVisibleHeaders(headers: Array<ITableHeaders>) {
  const { permission, isAdmin } = usePermission();

  // The same set the filter below removes, so the flag and the head can never
  // be resolved from different headers.
  const actionHeaders = headers.filter((header) => isActionId(header.id));

  // Two ways the column is absent, and both have to reach the cell.
  //
  // The header array not declaring one at all is the case this missed at first:
  // a list may omit `actionColumn()` outright for some users — Advertisers drops
  // it for a super admin — and the old test only looked at a header that was
  // present and failed its permission check. With no action header to find it
  // resolved to "visible", so the head rendered N columns while every row
  // rendered N+1, which is precisely the desync this hook exists to prevent.
  //
  // Every declared action header is tested, not just the first, because the
  // filter below strips them all — so one that fails takes the column with it.
  // That also means a gated header vetoes an ungated one, which is the safe
  // direction: a column nobody can act in beats buttons with no column.
  const hideActionColumn =
    actionHeaders.length === 0 ||
    actionHeaders.some(
      (header) =>
        header.permission && !isPermitted(header.permission, permission, isAdmin)
    );

  const visibleHeaders = hideActionColumn
    ? headers.filter((header) => !isActionId(header.id))
    : headers;

  // Report a second action column; do not try to repair it.
  //
  // Dropping the surplus header looked like the stronger fix — make the
  // mismatch impossible rather than merely name it — and it is wrong three ways.
  // Which one to drop is unknowable: keeping the first, while the row's cell is
  // last, leaves the counts matching and the columns *misaligned*, so `sync()`
  // reports a healthy table and every `nth-of-type` rule lands one column out —
  // the sticky column pinning a data cell while the real buttons scroll away.
  // The premise is unenforced: `<ActionCell />` is opt-in, and a hand-rolled row
  // emitting two action `<td>`s already agreed with a two-action head, so
  // dropping one would break a table that worked. And identity filtering cannot
  // even express it — the same header object listed twice survives
  // `header === actionHeaders[0]` for both entries.
  //
  // A mismatch is a fail-safe: `data-columns-match="false"` disables the
  // column-position rules and the table still renders, just plainly. A
  // wrong-but-plausible head is not. So leave the array alone and say so.
  if (IS_DEV && !hideActionColumn && actionHeaders.length > 1) {
    // Keyed on the whole array's ids, not the action ids alone: two unrelated
    // lists both pairing `action` with `actions` would otherwise share a key and
    // the second one would never be reported. Recomputed per render, so a header
    // array rebuilt on every render still dedupes.
    const shape = headers.map((header) => header.id).join("|");
    if (!warnedAboutDuplicateActionColumns.has(shape)) {
      warnedAboutDuplicateActionColumns.add(shape);
      console.warn(
        `[CustomTable] a header array declares ${actionHeaders.length} action ` +
          `columns (${actionHeaders.map((h) => h.id).join(", ")}). A row emits ` +
          "one <ActionCell />, so the head will have a column the body never " +
          "fills and every column-position rule for this table will be dropped."
      );
    }
  }

  return { visibleHeaders, actionColumnVisible: !hideActionColumn };
}
